import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: FindAPQFromCanonicalTask
   Cel: odczytaj a, p, q z f(x) = a(x - p)^2 + q
   Input: FractionInput użyty 3× (a, p, q)
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}
function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simplifyFrac(n, d){
  if (d === 0) return { n, d };
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function formatFracMixed(n, d){
  const sgn = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${sgn}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${sgn}\\dfrac{${N}}{${D}}`;
  return `${sgn}${whole}\\dfrac{${rem}}{${D}}`;
}
function canonicalLatex(a, pFrac, qFrac) {
  const aStr = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);

  const pStr = formatFracMixed(pFrac.n, pFrac.d);
  const pIsNeg = pFrac.n < 0;
  const pAbs = formatFracMixed(Math.abs(pFrac.n), pFrac.d);

  const qStr = formatFracMixed(qFrac.n, qFrac.d);
  const qIsNeg = qFrac.n < 0;
  const qAbs = formatFracMixed(Math.abs(qFrac.n), qFrac.d);

  const inner = pIsNeg ? `x + ${pAbs}` : `x - ${pStr}`;
  const tail  = qIsNeg ? ` - ${qAbs}` : ` + ${qStr}`;
  return `${aStr}\\big(${inner}\\big)^{2}${tail}`;
}

// Parsowanie: "3/4", "-2 1/3", "1,25", "-4.5", "7"
function parseFractionLike(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/\u2212/g, "-").replace(",", ".");

  // liczba mieszana A B/C
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1],10), B = parseInt(m[2],10), C = parseInt(m[3],10);
    if (!C) return null;
    const frac = B/C;
    return A >= 0 ? (A+frac) : (A-frac);
  }
  // zwykły ułamek P/Q
  m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const P = parseInt(m[1],10), Q = parseInt(m[2],10);
    if (!Q) return null;
    return P/Q;
  }
  // całkowita / dziesiętna
  if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}
function prettyRawToLatex(raw) {
  if (raw == null || String(raw).trim() === "") return "?";
  let s = String(raw).trim().replace(/\u2212/g, "-").replace(",", ".");
  // liczba mieszana
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) return `${m[1]}\\;\\dfrac{${m[2]}}{${m[3]}}`;
  // zwykły ułamek
  m = s.match(/^\s*([+-]?\d+)\\?\/\\?(\d+)\s*$/);
  if (m) return `\\dfrac{${m[1]}}{${m[2]}}`;
  // dziesiętna -> przecinek
  if (/^[+-]?\d+\.\d+$/.test(s)) return s.replace(".", "{,}");
  return s;
}

/* ---------- generator ---------- */
// Tworzymy kanoniczną biorąc losowe całkowite a,b,c (≠0), a potem:
// p = -b/(2a), q = -(b^2 - 4ac)/(4a)  — dokładne, ładne ułamki.
export function generateTask() {
  const a = randInt(-5, 5, new Set([0]));
  const b = randInt(-9, 9, new Set([0]));
  const c = randInt(-9, 9, new Set([0]));

  const delta = b*b - 4*a*c;
  const pFrac = simplifyFrac(-b, 2*a);
  const qFrac = simplifyFrac(-delta, 4*a);

  return {
    type: "FindAPQFromCanonicalTask",
    data: {
      a,
      pFrac, qFrac,
      p: pFrac.n / pFrac.d,
      q: qFrac.n / qFrac.d,
    }
  };
}

/* ---------- render ---------- */
export function renderTask({ data }) {
  const { a, pFrac, qFrac } = data;
  const body = canonicalLatex(a, pFrac, qFrac);
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci kanonicznej:{" "}
        <InlineMath math={`f(x) = ${body}`} />
      </div>
      <div>
        Odczytaj <InlineMath math={'a'} />, <InlineMath math={'p'} /> oraz{" "}
        <InlineMath math={'q'} />.
      </div>
    </div>
  );
}

/* ---------- input ---------- */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { a:{raw:""}, p:{raw:""}, q:{raw:""} };
  const group = { display: "grid", gap: 6 };

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={group}>
        <InlineMath math={'a ='} />
        <FractionInput
          value={val.a}
          onChange={(v)=>onChange({ ...(userAnswer??{}), a: v })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div style={group}>
        <InlineMath math={'p ='} />
        <FractionInput
          value={val.p}
          onChange={(v)=>onChange({ ...(userAnswer??{}), p: v })}
          disabled={disabled}
          label="p"
        />
      </div>
      <div style={group}>
        <InlineMath math={'q ='} />
        <FractionInput
          value={val.q}
          onChange={(v)=>onChange({ ...(userAnswer??{}), q: v })}
          disabled={disabled}
          label="q"
        />
      </div>
    </div>
  );
}

/* ---------- check ---------- */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const aRaw = userAnswer?.a?.raw ?? userAnswer?.a ?? "";
  const pRaw = userAnswer?.p?.raw ?? userAnswer?.p ?? "";
  const qRaw = userAnswer?.q?.raw ?? userAnswer?.q ?? "";

  const aParsed = parseFractionLike(aRaw);
  const pParsed = parseFractionLike(pRaw);
  const qParsed = parseFractionLike(qRaw);

  if ([aParsed, pParsed, qParsed].some(v => v == null || !Number.isFinite(v))) return false;

  const usedDecimal = (s) => /[.,]\d/.test(String(s ?? ""));
  const tolA = usedDecimal(aRaw) ? 1e-2 : 1e-9;
  const tolP = usedDecimal(pRaw) ? 1e-2 : 1e-9;
  const tolQ = usedDecimal(qRaw) ? 1e-2 : 1e-9;

  const okA = Math.abs(aParsed - data.a) < tolA;
  const okP = Math.abs(pParsed - data.p) < tolP;
  const okQ = Math.abs(qParsed - data.q) < tolQ;
  return okA && okP && okQ;
}

/* ---------- answer ---------- */
export function renderAnswer({ data }) {
  const a = data.a;
  const p = formatFracMixed(data.pFrac.n, data.pFrac.d);
  const q = formatFracMixed(data.qFrac.n, data.qFrac.d);
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`a = ${a},\\quad p = ${p},\\quad q = ${q}`} />
    </div>
  );
}

/* ---------- explanation ---------- */
export function renderExplanation({ data }) {
  const body = canonicalLatex(data.a, data.pFrac, data.qFrac);
  const signNote = 'W nawiasie jest \\(x - p\\). Jeśli widzisz \\(x + r\\), to znaczy, że \\(p=-r\\).';
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Porównaj z definicją postaci kanonicznej:</div>
      <BlockMath math={'f(x)=a(x-p)^2+q'} />
      <div><strong>Krok 2.</strong> Podana funkcja:</div>
      <BlockMath math={`f(x) = ${body}`} />
      <div style={{opacity:.85}}>{signNote}</div>
      <div><strong>Krok 3.</strong> Odczytujemy:</div>
      <BlockMath math={`a=${data.a},\\quad p=${formatFracMixed(data.pFrac.n,data.pFrac.d)},\\quad q=${formatFracMixed(data.qFrac.n,data.qFrac.d)}`} />
    </div>
  );
}

/* ---------- user answer (ładny podgląd) ---------- */
export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data;
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const raw = {
    a: userAnswer && (userAnswer.a?.raw ?? userAnswer.a) || "",
    p: userAnswer && (userAnswer.p?.raw ?? userAnswer.p) || "",
    q: userAnswer && (userAnswer.q?.raw ?? userAnswer.q) || "",
  };
  const empty = (s) => !String(s ?? "").trim();
  if (empty(raw.a) && empty(raw.p) && empty(raw.q)) return <i>(brak odpowiedzi)</i>;

  const show = (r) => {
    const s = String(r ?? "").trim();
    if (!s) return "?";
    return prettyRawToLatex(r);
  };

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`a = ${show(raw.a)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`p = ${show(raw.p)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`q = ${show(raw.q)}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
